import { useContext, useMemo } from "react"
import { Button, ButtonProps, StackProps, VStack } from "@chakra-ui/react"

import {
  selectActiveToken,
  selectDepositFromAnotherWallet,
  selectDepositStatus,
  selectHasDepositError,
  selectHasLoadedWalletBalances,
  selectTokenApprovalStatus,
  useDappSelector,
} from "#/redux-state"
import TransactionProgressLoader from "#/shared/components/TransactionProgressLoader"
import TransactionRejected, {
  TransactionRejectedProps,
} from "#/shared/components/TransactionRejected"
import { FlowContext } from "#/shared/context"
import {
  useBlockDepositButton,
  useDeposit,
  useDepositButtonStylesForDepositStatus,
} from "#/shared/hooks"

import DepositContinueButton from "./DepositContinueButton"

type DepositTokenButtonProps = StackProps & {
  showTokenName?: boolean
  buttonProps?: ButtonProps
  /**
   * Props applied to button in loading state
   */
  loaderButtonProps?: ButtonProps
  allowDepositRetry?: boolean
  rejectedStateProps?: Omit<TransactionRejectedProps, "onClick">
}

export default function DepositTokenButton({
  showTokenName = true,
  allowDepositRetry = true,
  buttonProps,
  loaderButtonProps,
  rejectedStateProps,
  ...props
}: DepositTokenButtonProps) {
  const depositStatus = useDappSelector(selectDepositStatus)
  const activeToken = useDappSelector(selectActiveToken)
  const tokenApprovalStatus = useDappSelector(selectTokenApprovalStatus)
  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)

  const { isEthereumFlow, isBitcoinFlow } = useContext(FlowContext)

  const { deposit } = useDeposit()

  const hasError = useDappSelector(selectHasDepositError)

  const depositFromAnotherWallet = useDappSelector(
    selectDepositFromAnotherWallet,
  )

  const shouldBlockButton = useBlockDepositButton()

  const isButtonBlocked = useMemo(
    () =>
      (!depositFromAnotherWallet && shouldBlockButton) ||
      tokenApprovalStatus === "pending" ||
      hasError ||
      !hasLoadedWalletBalances,
    [
      depositFromAnotherWallet,
      shouldBlockButton,
      tokenApprovalStatus,
      hasError,
      hasLoadedWalletBalances,
    ],
  )

  const showContinueButton = useMemo(
    () =>
      depositFromAnotherWallet ||
      isBitcoinFlow ||
      (isEthereumFlow && activeToken === "BTC"),
    [depositFromAnotherWallet, isBitcoinFlow, isEthereumFlow, activeToken],
  )

  const buttonText = showTokenName
    ? `Deposit ${isEthereumFlow ? activeToken : "BTC"}`
    : "Deposit"

  const finalButtonProps = useDepositButtonStylesForDepositStatus(
    buttonProps,
    loaderButtonProps,
  )

  const rejectedComponent = useMemo(
    () =>
      allowDepositRetry ? (
        <TransactionRejected
          onClick={() => deposit()}
          {...rejectedStateProps}
        />
      ) : (
        <TransactionRejected {...rejectedStateProps} />
      ),
    [allowDepositRetry, deposit, rejectedStateProps],
  )

  const buttonVariant = useMemo(
    () => (isButtonBlocked ? "disabled" : "primaryBlack"),
    [isButtonBlocked],
  )

  // Show continue button when:
  // - on bitcoin flow
  // - deposit from another wallet is enabled
  // - on ethereum blow and depositing BTC
  if (showContinueButton) {
    return <DepositContinueButton />
  }

  return (
    <VStack {...props}>
      <Button
        type="submit"
        variant={buttonVariant}
        color="primary.white.100"
        size={{ base: "medium", xl: "large" }}
        h="64px"
        isDisabled={isButtonBlocked}
        onClick={deposit}
        {...finalButtonProps}
      >
        {depositStatus === "pending" ? (
          <TransactionProgressLoader text="Waiting for deposit..." />
        ) : (
          buttonText
        )}
      </Button>
      {hasError && depositStatus !== "pending" && rejectedComponent}
    </VStack>
  )
}
